import React, { createContext, useContext, useState, useEffect } from 'react';
import THEME from '../config/theme';

// Create Theme Context
const ThemeContext = createContext({
  theme: 'light',
  toggleTheme: () => {},
  isDark: false,
  colors: THEME.colors.light,
  getColor: () => {},
});

// Custom hook to use theme
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

// Theme Provider Component
export const ThemeProvider = ({ children }) => {
  // Initialize theme from localStorage or system preference
  const [theme, setTheme] = useState(() => {
    const stored = localStorage.getItem('copilote-theme');
    if (stored) return stored;
    
    // Check system preference
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    
    return 'light';
  });

  const isDark = theme === 'dark';
  const colors = isDark ? THEME.colors.dark : THEME.colors.light;

  // Toggle theme function
  const toggleTheme = () => {
    setTheme(prev => {
      const newTheme = prev === 'light' ? 'dark' : 'light';
      localStorage.setItem('copilote-theme', newTheme);
      return newTheme;
    });
  };

  // Helper to get color from theme
  const getColor = (path) => {
    const parts = path.split('.');
    let color = colors;
    
    for (const part of parts) {
      color = color[part];
      if (!color) return colors.primary.DEFAULT;
    }
    
    return color;
  };

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    
    if (isDark) {
      root.classList.add('dark');
      root.classList.remove('light');
    } else {
      root.classList.add('light');
      root.classList.remove('dark');
    }

    // Set CSS custom properties for dynamic theming
    root.style.setProperty('--color-primary', colors.primary.DEFAULT);
    root.style.setProperty('--color-primary-light', colors.primary[300]);
    root.style.setProperty('--color-secondary', colors.secondary.DEFAULT);
    root.style.setProperty('--color-success', colors.accent.success);
    root.style.setProperty('--color-warning', colors.accent.warning);
    root.style.setProperty('--color-error', colors.accent.error);
    root.style.setProperty('--color-bg-primary', colors.background.primary);
    root.style.setProperty('--color-bg-secondary', colors.background.secondary);
    root.style.setProperty('--color-text-primary', colors.text.primary);
    root.style.setProperty('--color-text-secondary', colors.text.secondary);
    root.style.setProperty('--color-border', colors.border.DEFAULT);
    root.style.setProperty('--gradient-primary', colors.gradient.primary);
    
    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', colors.background.primary);
    }
  }, [theme, isDark, colors]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e) => {
      const stored = localStorage.getItem('copilote-theme');
      // Only auto-switch if user hasn't manually set a preference
      if (!stored) {
        setTheme(e.matches ? 'dark' : 'light');
      }
    };
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const value = {
    theme,
    toggleTheme,
    isDark,
    colors,
    getColor,
    THEME, // Expose full theme object
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
